import React, { useState, useEffect } from 'react';
import './App.css';
import ApiClient, { UserInfo, AppStatus, getDefaultApiHostname, normalizeApiHostname } from './api/client';
import Dashboard from './components/Dashboard';
import ChannelManager from './components/ChannelManager';
import ApiKeyManager from './components/ApiKeyManager';
import SubmissionManager from './components/SubmissionManager';
import IPManagement from './components/IPManagement';
import CostAnalytics from './components/CostAnalytics';
import Analytics from './components/Analytics';
import ReputationManager from './components/ReputationManager';
import Settings from './components/Settings';
import SetupScreen from './components/SetupScreen';
import StatusBanner from './components/StatusBanner';
import DocsPortal from './components/DocsPortal';
import ThemeToggle from './components/ThemeToggle';
import {
  BookIcon,
  ChannelIcon,
  ChartIcon,
  CloseIcon,
  CoinIcon,
  FormIcon,
  GlobeIcon,
  InboxIcon,
  KeyIcon,
  MenuIcon,
  SettingsIcon,
  ShieldIcon,
} from './components/icons';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import ConsoleSignIn from './platform/ConsoleSignIn';
import { TokenSession } from './platform/tokenSession';
import BusinessSwitcher from './platform/BusinessSwitcher';

type Tab = 'forms' | 'channels' | 'submissions' | 'apikeys' | 'ipmanagement' | 'reputation' | 'analytics' | 'costs' | 'docs' | 'settings';

interface NavItem {
  id: Tab;
  label: string;
  permission: string;
  Icon: React.FC<{ size?: number }>;
}

// Sidebar groups. Permissions mirror the per-tab checks used to render content below.
const NAV_GROUPS: Array<{ label: string; items: NavItem[] }> = [
  {
    label: 'Operate',
    items: [
      { id: 'submissions', label: 'Submissions', permission: 'submissions.view', Icon: InboxIcon },
      { id: 'forms', label: 'Forms', permission: 'forms.view', Icon: FormIcon },
      { id: 'channels', label: 'Channels', permission: 'channels.view', Icon: ChannelIcon },
    ],
  },
  {
    label: 'Security',
    items: [
      { id: 'reputation', label: 'Reputation', permission: 'reputation.view', Icon: ShieldIcon },
      { id: 'ipmanagement', label: 'IP Management', permission: 'ip.view', Icon: GlobeIcon },
    ],
  },
  {
    label: 'Insights',
    items: [
      { id: 'analytics', label: 'Analytics', permission: 'analytics.view', Icon: ChartIcon },
      { id: 'costs', label: 'Costs', permission: 'costs.view', Icon: CoinIcon },
    ],
  },
  {
    label: 'Admin',
    items: [
      { id: 'apikeys', label: 'API Keys', permission: 'api_keys.view', Icon: KeyIcon },
      { id: 'docs', label: 'Docs', permission: 'api_keys.view', Icon: BookIcon },
      { id: 'settings', label: 'Settings', permission: 'settings.view', Icon: SettingsIcon },
    ],
  },
];

interface AppContentProps {
  apiClient: ApiClient;
  appStatus: AppStatus | null;
  hostname: string;
  onReconfigure: () => void;
  onLogout: () => void;
  session?: TokenSession | null;
  onSwitchBusiness?: (next: TokenSession) => void;
}

const AppContent: React.FC<AppContentProps> = ({ apiClient, appStatus, hostname, onReconfigure, onLogout, session, onSwitchBusiness }) => {
  const { hasPermission, roleLabel } = usePermissions();
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile navigation drawer with Escape.
  useEffect(() => {
    if (!navOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [navOpen]);

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const saved = localStorage.getItem('activeTab') as Tab | null;
    return saved && ['forms', 'channels', 'submissions', 'apikeys', 'ipmanagement', 'reputation', 'analytics', 'costs', 'docs', 'settings'].includes(saved) ? saved : 'forms';
  });

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Redirect to allowed tab if current tab is not permitted
  useEffect(() => {
    const tabPermissions: Record<Tab, string> = {
      forms: 'forms.view',
      channels: 'channels.view',
      submissions: 'submissions.view',
      apikeys: 'api_keys.view',
      ipmanagement: 'ip.view',
      reputation: 'reputation.view',
      analytics: 'analytics.view',
      costs: 'costs.view',
      docs: 'api_keys.view',
      settings: 'settings.view',
    };

    if (!hasPermission(tabPermissions[activeTab])) {
      // Find first allowed tab
      const allowedTab = (Object.keys(tabPermissions) as Tab[]).find(
        tab => hasPermission(tabPermissions[tab])
      );
      if (allowedTab) {
        setActiveTab(allowedTab);
      }
    }
  }, [activeTab, hasPermission]);

  const navigateToSettings = () => {
    if (hasPermission('settings.view')) {
      setActiveTab('settings');
    }
  };

  const navigateToSubmissions = (params: { ipId?: number; asn?: number }) => {
    if (hasPermission('submissions.view')) {
      // Update URL params before switching tab
      const url = new URL(window.location.href);
      url.searchParams.delete('ipId');
      url.searchParams.delete('asn');
      if (params.ipId) url.searchParams.set('ipId', String(params.ipId));
      if (params.asn) url.searchParams.set('asn', String(params.asn));
      window.history.replaceState({}, '', url.toString());
      setActiveTab('submissions');
    }
  };

  const visibleGroups = NAV_GROUPS
    .map(group => ({ ...group, items: group.items.filter(item => hasPermission(item.permission)) }))
    .filter(group => group.items.length > 0);
  const activeGroup = NAV_GROUPS.find(group => group.items.some(item => item.id === activeTab));
  const activeItem = activeGroup?.items.find(item => item.id === activeTab);

  return (
    <div className={`app ${navOpen ? 'nav-open' : ''}`}>
      <aside className="app-sidebar" id="app-sidebar">
        <div className="sidebar-brand">
          <div className="brand-lockup">
            <img
              src="/form-pipeline-icon.png"
              alt=""
              className="brand-mark"
              aria-hidden="true"
            />
            <div className="brand-text">
              <span className="brand-name">Form Pipeline</span>
              <span className="brand-eyebrow">Operations Console</span>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn sidebar-close"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="app-nav" aria-label="Main navigation">
          {visibleGroups.map(group => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group-label">{group.label}</div>
              {group.items.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`nav-button ${activeTab === id ? 'active' : ''}`}
                  aria-current={activeTab === id ? 'page' : undefined}
                  onClick={() => {
                    setActiveTab(id);
                    setNavOpen(false);
                  }}
                >
                  <Icon />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      <div className="app-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <div className="app-main">
        <header className="app-header app-topbar">
          <button
            type="button"
            className="icon-btn nav-toggle"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="app-sidebar"
          >
            <MenuIcon size={18} />
          </button>
          <div className="topbar-title">
            {activeGroup && (
              <>
                <span className="topbar-section">{activeGroup.label}</span>
                <span className="topbar-sep" aria-hidden="true">/</span>
              </>
            )}
            <h1 className="topbar-page">{activeItem?.label ?? 'Form Pipeline'}</h1>
          </div>
          <div className="topbar-actions">
            {session && onSwitchBusiness && <BusinessSwitcher session={session} onSwitch={onSwitchBusiness} />}
            <ThemeToggle />
            <div className="connection-menu">
              <button
                type="button"
                className="connection-button"
                onClick={() => setConnectionOpen(open => !open)}
                aria-expanded={connectionOpen}
                title={hostname}
              >
                <span className="connection-dot" aria-hidden="true" />
                <span className="connection-host">{hostname.replace(/^https?:\/\//, '')}</span>
                {roleLabel && <span className="connection-role">{roleLabel}</span>}
              </button>
              {connectionOpen && (
                <div className="connection-popover">
                  <div className="connection-popover-label">Backend</div>
                  <div className="connection-popover-host">{hostname}</div>
                  <div className="connection-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setConnectionOpen(false);
                        onReconfigure();
                      }}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger-soft"
                      onClick={() => {
                        setConnectionOpen(false);
                        onLogout();
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <StatusBanner
          status={appStatus}
          onNavigateToSettings={hasPermission('settings.view') ? navigateToSettings : undefined}
        />

        <main className="app-content">
          {activeTab === 'forms' && hasPermission('forms.view') && (
            <Dashboard apiClient={apiClient} />
          )}
          {activeTab === 'channels' && hasPermission('channels.view') && (
            <ChannelManager apiClient={apiClient} />
          )}
          {activeTab === 'submissions' && hasPermission('submissions.view') && (
            <SubmissionManager apiClient={apiClient} />
          )}
          {activeTab === 'apikeys' && hasPermission('api_keys.view') && (
            <ApiKeyManager apiClient={apiClient} />
          )}
          {activeTab === 'ipmanagement' && hasPermission('ip.view') && (
            <IPManagement apiClient={apiClient} onNavigateToSubmissions={navigateToSubmissions} />
          )}
          {activeTab === 'reputation' && hasPermission('reputation.view') && (
            <ReputationManager apiClient={apiClient} />
          )}
          {activeTab === 'analytics' && hasPermission('analytics.view') && (
            <Analytics apiClient={apiClient} />
          )}
          {activeTab === 'costs' && hasPermission('costs.view') && (
            <CostAnalytics apiClient={apiClient} />
          )}
          {activeTab === 'docs' && hasPermission('api_keys.view') && (
            <DocsPortal apiKeyAvailable={Boolean(localStorage.getItem('apiKey'))} />
          )}
          {activeTab === 'settings' && hasPermission('settings.view') && (
            <Settings apiClient={apiClient} />
          )}
        </main>
      </div>
    </div>
  );
};

/**
 * Who the caller is and what they may do, from /api/me, with fallbacks for older
 * backends. Shared by the API-key and signed-in connections.
 */
async function loadUserInfo(client: ApiClient): Promise<UserInfo> {
  try {
    const info = await client.getMe();
    console.log('User info:', info);
    // Merge with defaults for any missing permission categories
    const isAdmin = info.role === 'admin';
    const mergedPermissions = {
      submissions: info.permissions?.submissions ?? { view: true, moderate: true, delete: true, delete_permanent: isAdmin },
      ip: info.permissions?.ip ?? { view: true, manage: true },
      forms: info.permissions?.forms ?? { view: true, create: true, edit: true, delete: isAdmin },
      channels: info.permissions?.channels ?? { view: true, manage: isAdmin },
      integrations: info.permissions?.integrations ?? { view: true, manage: isAdmin },
      analytics: info.permissions?.analytics ?? { view: true },
      costs: info.permissions?.costs ?? { view: isAdmin },
      api_keys: info.permissions?.api_keys ?? { view: isAdmin, manage: isAdmin },
      settings: info.permissions?.settings ?? { view: isAdmin, manage: isAdmin },
      reputation: info.permissions?.reputation ?? { view: true, manage: isAdmin },
    };
    return { ...info, permissions: mergedPermissions };
  } catch (err) {
    console.error('Failed to fetch user info:', err);
    // Fallback: try old permission check for backwards compatibility
    try {
      const perms = await client.checkPermissions();
      // Create a minimal userInfo from old API
      return {
        authenticated: true,
        role: perms.is_admin ? 'admin' : 'user',
        role_label: perms.is_admin ? 'Admin' : 'User',
        permissions: {
          submissions: { view: true, moderate: true, delete: true, delete_permanent: perms.is_admin },
          ip: { view: true, manage: true },
          forms: { view: true, create: true, edit: true, delete: perms.is_admin },
          channels: { view: true, manage: perms.is_admin },
          integrations: { view: true, manage: perms.is_admin },
          analytics: { view: true },
          costs: { view: perms.is_admin },
          api_keys: { view: perms.is_admin, manage: perms.is_admin },
          settings: { view: perms.is_admin, manage: perms.is_admin },
          reputation: { view: true, manage: perms.is_admin },
        },
      };
    } catch (fallbackErr) {
      console.error('Fallback permission check failed:', fallbackErr);
      // Set minimal permissions matching User role
      return {
        authenticated: true,
        role: 'user',
        role_label: 'User',
        permissions: {
          submissions: { view: true, moderate: true, delete: true, delete_permanent: false },
          ip: { view: true, manage: true },
          forms: { view: true, create: true, edit: true, delete: false },
          channels: { view: true, manage: false },
          integrations: { view: true, manage: false },
          analytics: { view: true },
          costs: { view: false },
          api_keys: { view: false, manage: false },
          settings: { view: false, manage: false },
          reputation: { view: true, manage: false },
        },
      };
    }
  }
}

function App() {
  const [apiKey, setApiKey] = useState<string>('');
  const [hostname, setHostname] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [apiClient, setApiClient] = useState<ApiClient | null>(null);
  const [appStatus, setAppStatus] = useState<AppStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [session, setSession] = useState<TokenSession | null>(null);

  // Signed in (specs/002-data-plane-tokens): talk to the account's data plane
  // with short-lived tokens from the session. Returning to sign-in if it ends.
  const connectSession = async (next: TokenSession) => {
    const signedOut = () => {
      setSession(null);
      setApiClient(null);
      setUserInfo(null);
      setAppStatus(null);
      setShowSignIn(true);
    };
    const client = new ApiClient('', next.apiBaseUrl, (force) => next.accessToken(force), signedOut);
    setSession(next);
    setHostname(normalizeApiHostname(next.apiBaseUrl));
    setApiClient(client);
    setShowSignIn(false);
    setShowSetup(false);
    setError(null);
    setUserInfo(await loadUserInfo(client));
    try {
      setAppStatus(await client.getStatus());
    } catch (err) {
      console.error('Failed to fetch app status:', err);
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Load credentials from localStorage
        const storedKey = localStorage.getItem('apiKey');
        const storedHostname = normalizeApiHostname(localStorage.getItem('apiHostname') || getDefaultApiHostname());

        if (!storedKey) {
          setHostname(storedHostname);
          // No API key here: resume a sign-in session if one is alive, else sign in.
          const resumed = await TokenSession.resume();
          if (resumed) {
            await connectSession(resumed);
          } else {
            setShowSignIn(true);
          }
          setLoading(false);
          return;
        }

        setApiKey(storedKey);
        setHostname(storedHostname);
        const client = new ApiClient(storedKey, storedHostname);
        setApiClient(client);

        setUserInfo(await loadUserInfo(client));

        // Fetch app status for warnings banner
        try {
          const status = await client.getStatus();
          setAppStatus(status);
        } catch (err) {
          console.error('Failed to fetch app status:', err);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  const handleSetup = async (newApiKey: string, newHostname: string) => {
    try {
      // Store credentials
      localStorage.setItem('apiKey', newApiKey);
      const normalizedHostname = normalizeApiHostname(newHostname);
      localStorage.setItem('apiHostname', normalizedHostname);

      setApiKey(newApiKey);
      setHostname(normalizedHostname);
      const client = new ApiClient(newApiKey, normalizedHostname);
      setApiClient(client);
      setShowSetup(false);
      setError(null);

      // Fetch user info
      try {
        const info = await client.getMe();
        // Merge with defaults for any missing permission categories
        const isAdmin = info.role === 'admin';
        const mergedPermissions = {
          submissions: info.permissions?.submissions ?? { view: true, moderate: true, delete: true, delete_permanent: isAdmin },
          ip: info.permissions?.ip ?? { view: true, manage: true },
          forms: info.permissions?.forms ?? { view: true, create: true, edit: true, delete: isAdmin },
          channels: info.permissions?.channels ?? { view: true, manage: isAdmin },
          integrations: info.permissions?.integrations ?? { view: true, manage: isAdmin },
          analytics: info.permissions?.analytics ?? { view: true },
          costs: info.permissions?.costs ?? { view: isAdmin },
          api_keys: info.permissions?.api_keys ?? { view: isAdmin, manage: isAdmin },
          settings: info.permissions?.settings ?? { view: isAdmin, manage: isAdmin },
          reputation: info.permissions?.reputation ?? { view: true, manage: isAdmin },
        };
        setUserInfo({ ...info, permissions: mergedPermissions });
      } catch (err) {
        // Fallback to old API
        const perms = await client.checkPermissions();
        setUserInfo({
          authenticated: true,
          role: perms.is_admin ? 'admin' : 'user',
          role_label: perms.is_admin ? 'Admin' : 'User',
          permissions: {
            submissions: { view: true, moderate: true, delete: true, delete_permanent: perms.is_admin },
            ip: { view: true, manage: true },
            forms: { view: true, create: true, edit: true, delete: perms.is_admin },
            channels: { view: true, manage: perms.is_admin },
            integrations: { view: true, manage: perms.is_admin },
            analytics: { view: true },
            costs: { view: perms.is_admin },
            api_keys: { view: perms.is_admin, manage: perms.is_admin },
            settings: { view: perms.is_admin, manage: perms.is_admin },
            reputation: { view: true, manage: perms.is_admin },
          },
        });
      }

      // Fetch app status
      try {
        const status = await client.getStatus();
        setAppStatus(status);
      } catch (err) {
        console.error('Failed to fetch app status:', err);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Connecting to Form Pipeline...</p>
      </div>
    );
  }

  if (showSignIn) {
    return (
      <ConsoleSignIn
        onReady={(next) => {
          connectSession(next).catch((err) => setError(err instanceof Error ? err.message : 'Failed to connect'));
        }}
        onUseApiKey={() => {
          setShowSignIn(false);
          setShowSetup(true);
        }}
      />
    );
  }

  if (showSetup) {
    return (
      <SetupScreen
        onSetup={handleSetup}
        initialKey={apiKey}
        initialHostname={hostname}
        onSignIn={() => {
          setShowSetup(false);
          setShowSignIn(true);
        }}
      />
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button
          onClick={() => {
            setShowSetup(true);
            setError(null);
          }}
          className="btn btn-primary"
        >
          Reconfigure Connection
        </button>
      </div>
    );
  }

  if (!apiClient) {
    return null;
  }

  return (
    <PermissionProvider userInfo={userInfo}>
      <AppContent
        // A new business is a fresh console: every page reloads its data.
        key={session?.accountId ?? 'api-key'}
        session={session}
        onSwitchBusiness={(next) => {
          connectSession(next).catch((err) => setError(err instanceof Error ? err.message : 'Failed to connect'));
        }}
        apiClient={apiClient}
        appStatus={appStatus}
        hostname={hostname}
        onReconfigure={() => {
          setShowSetup(true);
          setError(null);
        }}
        onLogout={() => {
          if (session) {
            session.signOut();
            setSession(null);
          } else {
            localStorage.removeItem('apiKey');
            setApiKey('');
          }
          setApiClient(null);
          setUserInfo(null);
          setAppStatus(null);
          setShowSignIn(true);
          setError(null);
        }}
      />
    </PermissionProvider>
  );
}

export default App;
